'use client';
import type { ComponentProps } from 'react';
import React, { useCallback, useState } from 'react';
import { Button, Flex, Spinner, Text } from '@skin-seoul/design-system';
import { CART_QUERY_KEYS, ProductType, useCartService, useRegionCurrency } from '@skin-seoul/service';
import { useProductCard } from '../ProductCardContext';
import { LucideIcon } from '../../../components/LucideIcon';
import { AnimatePresence, motion } from 'motion/react';
import {
  BridgeApi,
  Condition,
  useDebouncedNumberCommit,
  useIsTouchDevice,
  useMutationLoading,
  usePageLogger,
} from '@skin-seoul/utils';

const MotionFlex = motion.create(Flex);
const MotionButton = motion.create(Button);

type ProductCardAddToCartProps = ComponentProps<typeof MotionFlex> & {
  onAddToCartClick?: () => void;
};

const ProductCardAddToCart = ({ onAddToCartClick, ...props }: ProductCardAddToCartProps) => {
  const { sku, productType, status, href, name, brand, originalPrice, salePrice, productId, category } = useProductCard();
  const isOutOfStock = status === 'outofstock';
  const { currency } = useRegionCurrency();
  const { useAddToCart, useGetCartQuery, useUpdateCartItem, useDeleteCartItem } = useCartService();
  const { data: cart } = useGetCartQuery({ currency });
  const { mutate: addToCart, isPending: isAddToCartPending } = useAddToCart({ currency });
  const { mutate: updateCartItem, isPending: isUpdateCartItemPending } = useUpdateCartItem({ currency });
  const { mutate: deleteCartItem, isPending: isDeleteCartItemPending } = useDeleteCartItem({ currency });
  const [hovered, setHovered] = useState(false);
  const isTouch = useIsTouchDevice();
  const [tappedOpen, setTappedOpen] = useState(false);
  const isCartLoading = useMutationLoading(CART_QUERY_KEYS.default());
  const logger = usePageLogger();

  const currentQuantity = cart?.cart?.item?.find((item) => item.sku === sku)?.quantity || 0;
  const isLoading = isAddToCartPending || isUpdateCartItemPending || isDeleteCartItemPending;

  const COMMIT_DEBOUNCE_MS = 300;

  const { value: displayQuantity, updateBy } = useDebouncedNumberCommit({
    initialValue: currentQuantity,
    delayMs: COMMIT_DEBOUNCE_MS,
    max: 999,
    onCommit: (nextQuantity) => {
      if (!sku || productType === ProductType.Variable || isOutOfStock) {
        return;
      }

      const itemKey = cart?.cart?.item?.find((item) => item.sku === sku)?.key;

      if (nextQuantity <= 0) {
        if (itemKey) {
          deleteCartItem(
            { itemKey, currency },
            {
              onSuccess: () => {
                if (BridgeApi.isWebview()) {
                  BridgeApi.cartUpdated();
                }
              },
            },
          );
        }
        return;
      }

      if (!itemKey) {
        addToCart(
          { sku, quantity: nextQuantity, currency },
          {
            onSuccess: () => {
              if (BridgeApi.isWebview()) {
                BridgeApi.cartUpdated();
              }
            },
          },
        );
        return;
      }

      updateCartItem(
        { itemKey, quantity: nextQuantity, currency },
        {
          onSuccess: () => {
            if (BridgeApi.isWebview()) {
              BridgeApi.cartUpdated();
            }
          },
        },
      );
    },
  });

  const openOptions = (!isTouch && hovered) || displayQuantity > 0 || isLoading || (isTouch && tappedOpen);

  const trackViewItemEvent = useCallback(() => {
    if (productType !== ProductType.Variable || !logger) {
      return;
    }

    const itemPrice = salePrice ?? originalPrice;

    logger.event('PLC_view_item', 'click', {
      name: 'PLC_view_item',
      'x-fb-cd-content_type': 'product',
      'x-fb-cd-content_name': name,
      'x-fb-cd-content_ids': productId || sku,
      value: itemPrice,
      currency,
      items: [
        {
          item_id: productId || sku,
          item_name: name,
          item_brand: brand || '',
          item_sku: sku,
          price: itemPrice.toString(),
          item_category: category?.[0] || '',
          item_category2: category?.[1] || '',
        },
      ],
      event_name: 'view_item',
      event_time: Math.round(new Date().getTime() / 1000),
    });
  }, [productType, logger, salePrice, originalPrice, productId, sku, name, brand, category, currency]);

  const trackAddToCartEvent = useCallback(
    (quantity: number) => {
      if (!logger) {
        return;
      }

      const itemPrice = salePrice ?? originalPrice;
      const totalPrice = itemPrice * quantity;

      logger.click('add_to_cart', {
        'x-fb-cd-content_type': 'product',
        'x-fb-cd-content_name': name,
        'x-fb-cd-content_ids': productId || sku,
        value: totalPrice.toString(),
        currency,
        items: [
          {
            item_id: productId || sku,
            item_name: name,
            item_brand: brand || '',
            item_sku: sku,
            price: itemPrice.toString(),
            item_category: category?.[0] || '',
            item_category2: category?.[1] || '',
          },
        ],
        event_name: 'add_to_cart',
        event_time: Math.round(new Date().getTime() / 1000),
        product_sku: sku,
      });
    },
    [logger, salePrice, originalPrice, name, productId, sku, currency, brand, category],
  );

  const handleNavigate = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e?.stopPropagation();
      e?.preventDefault();
      window.location.href = href;
    },
    [href],
  );

  const handleAddClick = useCallback(
    (e?: React.MouseEvent<HTMLButtonElement>) => {
      e?.stopPropagation();
      e?.preventDefault();
      if (!sku || isLoading || productType === ProductType.Variable || isOutOfStock) {
        return;
      }
      trackAddToCartEvent(1);
      onAddToCartClick?.();
      updateBy(1);
    },
    [sku, isLoading, productType, updateBy, trackAddToCartEvent, onAddToCartClick, isOutOfStock],
  );

  const handleMinusClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e?.stopPropagation();
      e?.preventDefault();
      if (!sku || isLoading) {
        return;
      }
      updateBy(-1);
    },
    [sku, isLoading, updateBy],
  );

  const handlePlusClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      const isVariableProduct = productType === ProductType.Variable;
      const isFirstTapOnMobile = isTouch && !tappedOpen;
      const isSecondTapOnMobile = isTouch && tappedOpen;
      const shouldNavigateOnDesktop = !isTouch && openOptions;

      if (!isVariableProduct) {
        handleAddClick(e);
        return;
      }

      if (isFirstTapOnMobile) {
        e?.stopPropagation();
        e?.preventDefault();
        setTappedOpen(true);
        return;
      }

      if (isSecondTapOnMobile || shouldNavigateOnDesktop) {
        trackViewItemEvent();
        handleNavigate(e);
      }
    },
    [productType, isTouch, tappedOpen, openOptions, handleAddClick, handleNavigate, trackViewItemEvent],
  );

  return (
    <Flex justify="flex-end" width="100%" height="fit-content">
      <MotionFlex
        onHoverStart={() => !isTouch && setHovered(true)}
        onHoverEnd={() => {
          setHovered(false);
          if (isTouch) {
            setTappedOpen(false);
          }
        }}
        width="100%"
        alignItems="center"
        {...props}
      >
        <MotionFlex
          key="expanded"
          justifyContent="flex-end"
          alignItems="center"
          width="100%"
          gap={2}
          backgroundColor={openOptions ? 'primary.75' : 'transparent'}
          borderRadius="20px"
        >
          <AnimatePresence initial={false} mode="popLayout">
            {openOptions && (
              <MotionFlex
                key="minus-group"
                alignItems="center"
                gap={2}
                w="100%"
                justifyContent="space-between"
                initial={{ x: 48, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                exit={{ x: 48, opacity: 0 }}
                transition={{ type: 'spring', stiffness: 500, damping: 36, mass: 0.6 }}
              >
                <Condition
                  expression={productType === ProductType.Variable}
                  then={
                    <Text
                      textStyle="label.small"
                      paddingLeft={{ base: 5 }}
                      color="primary.400"
                      fontWeight="regular"
                      textAlign="center"
                      w="100%"
                    >
                      Available Options
                    </Text>
                  }
                  else={
                    <>
                      <MotionButton
                        onClick={handleMinusClick}
                        height="40px"
                        width="40px"
                        bg="primary.400"
                        color="white"
                        borderRadius="20px"
                        px={3}
                        boxShadow="0 4px 4px 0 rgba(111, 63, 215, 0.25)"
                        disabled={isLoading || isCartLoading}
                      >
                        <LucideIcon boxSize={5} name="Minus" color="white" />
                      </MotionButton>
                      <Condition
                        expression={isLoading}
                        then={
                          <Flex w="100%" justifyContent="center" alignItems="center">
                            <Spinner size="sm" color="primary.500" animationDuration="0.8s" />
                          </Flex>
                        }
                        else={
                          <Text
                            textStyle="label.medium"
                            color="neutral.700"
                            w="100%"
                            textAlign="center"
                            fontWeight="regular"
                          >
                            {displayQuantity}
                          </Text>
                        }
                      />
                    </>
                  }
                />
              </MotionFlex>
            )}
          </AnimatePresence>

          <Button
            onClick={handlePlusClick}
            height="40px"
            width="40px"
            bg="primary.400"
            color="white"
            borderRadius="20px"
            px={3}
            boxShadow="0 4px 4px 0 rgba(111, 63, 215, 0.25)"
            disabled={isLoading || isCartLoading || isOutOfStock}
          >
            <LucideIcon boxSize={5} name="Plus" color="white" />
          </Button>
        </MotionFlex>
      </MotionFlex>
    </Flex>
  );
};

export default ProductCardAddToCart;
